import type { LetterCoverageGrid } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { meQuery } from '../../api';
import { letterCoverageQuery } from '../../api/letter-coverage';
import { letterCellHref } from '../../settings/letters/links';
import { cellWords, MIX_NAME, problemWords } from '../../settings/letters/words';
import { canOpenArea } from '../../settings/nav';
import { useMayRead, useSetting } from '../../settings/parts';
import { TwoColumns } from '../../shell';
import { Card, EmptyState, formatCount, plural, StatusPill, type StatusTone } from '../../ui';
import { TEMPLATES_HREF } from '../letters/presenter';
import { SettingChange } from '../SettingChange';
import s from '../Ops.module.css';

// SA3's day-one facts on the queue's Letters view (WP-BOARDS-OPS): whether each award type has a
// letter in use for every signer and cash and options mix (APP-130: coverage, as Settings ›
// Elections and letters and the go-live step count it), who gets each letter by default, and where
// each merge field comes from. They show before any award reaches letters, and stay while there is
// work. Read-only: letters and defaults are changed in Settings › Elections and letters.

const LETTER_TYPES = ['SDB', 'EDB', 'PAA', 'TAA'] as const;
type LetterType = (typeof LETTER_TYPES)[number];

const TYPE_NAME: Readonly<Record<LetterType, string>> = {
  SDB: 'Sector Discretionary Bonus',
  EDB: 'Executive Discretionary Bonus',
  PAA: 'Professional Achievement Award',
  TAA: 'Team Achievement Award',
};

const RECIPIENT: Readonly<Record<string, string>> = {
  requester: 'The person who entered it',
  manager: 'The employee’s manager',
  employee: 'The employee',
};

/** How many of a type's letters that aren't ready are named before "and n more". */
const NAMED = 3;

/** A letter that isn't ready: who signs it and what it reads, or why it has no one to sign it. */
interface NotReady {
  key: string;
  words: string;
  /** Its cell in Settings › Elections and letters; none for a row without a signer. */
  href: string | null;
}

function notReady(grid: LetterCoverageGrid): NotReady[] {
  return grid.rows.flatMap((row, i): NotReady[] => {
    if (row.problem !== null)
      return [
        { key: `${grid.awardType}-problem-${String(i)}`, words: problemWords(row), href: null },
      ];
    const who = [row.sector?.name, row.signer?.name].filter((w) => w !== undefined).join(' · ');
    // APP-221: a cell that needs no letter has nothing to finish.
    return row.cells
      .filter((cell) => cell.state !== 'ready' && cell.state !== 'not_needed')
      .map((cell) => ({
        key: cell.key,
        words: `${who} · ${MIX_NAME[cell.mix]} · ${cellWords(cell).label}`,
        href: letterCellHref(cell.key),
      }));
  });
}

function typeStatus(grid: LetterCoverageGrid): { tone: StatusTone; label: string } {
  const left = grid.counts.total - grid.counts.ready;
  if (grid.counts.total === 0) return { tone: 'neutral', label: 'None needed yet' };
  if (left === 0) return { tone: 'done', label: 'Ready' };
  return { tone: 'problem', label: `${plural(left, 'letter', 'letters')} to finish` };
}

function readyWords(grid: LetterCoverageGrid): string {
  const { ready, total } = grid.counts;
  if (total === 0) return 'No letters needed yet';
  return `${formatCount(ready)} of ${plural(total, 'letter', 'letters')} ready`;
}

/** One award type's letters: how many are ready, and the first few that aren't, each to its cell. */
function TypeLetters({
  type,
  grid,
  canOpen,
}: {
  type: LetterType;
  grid: LetterCoverageGrid | undefined;
  canOpen: boolean;
}): ReactElement {
  if (grid === undefined)
    return (
      <li className={s.letterType}>
        <span className={s.groupLabel}>{TYPE_NAME[type]}</span>
        <span className={s.muted}>Not shown to you</span>
      </li>
    );
  const status = typeStatus(grid);
  const left = notReady(grid);
  return (
    <li className={s.letterType}>
      <span className={s.groupLabel}>{TYPE_NAME[type]}</span>
      <div className={s.templateVersion}>
        <span className={s.templateVersionText}>
          <span className={s.strong}>{readyWords(grid)}</span>
          {left.slice(0, NAMED).map((item) => (
            <span key={item.key} className={s.sub}>
              {item.href !== null && canOpen ? (
                <Link href={item.href} className={s.inlineLink}>
                  {item.words}
                </Link>
              ) : (
                item.words
              )}
            </span>
          ))}
          {left.length > NAMED && (
            <span className={s.sub}>and {formatCount(left.length - NAMED)} more</span>
          )}
        </span>
        <StatusPill size="sm" tone={status.tone} label={status.label} />
      </div>
    </li>
  );
}

function LettersInUseCard(): ReactElement {
  const me = useQuery(meQuery).data;
  const mayRead = useMayRead('letters.templates');
  const q = useQuery({ ...letterCoverageQuery, enabled: mayRead === true });
  const canOpen = me !== undefined && canOpenArea(me, 'elections-letters');
  const help = {
    shows:
      'Whether each award type has a letter in use for each signer and cash and options mix, and which letters aren’t ready yet.',
    canDo:
      'Open a letter that isn’t ready to upload it or review its sample. Letters are uploaded, checked and approved in Settings › Elections and letters.',
  };
  const grids = (q.data?.grids ?? []).filter((g) =>
    (LETTER_TYPES as readonly string[]).includes(g.awardType),
  );
  return (
    <Card title="Letters in use" help={help}>
      {mayRead === false ? (
        <EmptyState
          variant="not-set"
          art="letter"
          size="sm"
          headline="Letters aren’t shown to you"
          body="The Stock Admin team sees which letters are in use for each signer and mix."
        />
      ) : q.isError ? (
        <p className={s.muted}>The letters didn’t load. Reload the page to try again.</p>
      ) : q.data === undefined ? (
        <p className={s.muted}>Loading the letters…</p>
      ) : grids.length === 0 ? (
        <EmptyState
          variant="not-set"
          art="letter"
          size="sm"
          headline="No letters yet"
          body="Each award type needs a letter for each signer and cash and options mix, with the signature already in it. Add them before the first award reaches letters."
          {...(canOpen && {
            action: { label: 'Open Elections and letters', href: TEMPLATES_HREF },
          })}
        />
      ) : (
        <ul className={s.letterTypes} aria-label="Letters by award type">
          {LETTER_TYPES.map((type) => (
            <TypeLetters
              key={type}
              type={type}
              grid={grids.find((g) => g.awardType === type)}
              canOpen={canOpen}
            />
          ))}
        </ul>
      )}
      <p className={s.muted}>
        Letters are kept in{' '}
        <Link href={TEMPLATES_HREF} className={s.inlineLink}>
          Settings › Elections and letters
        </Link>
        .
      </p>
    </Card>
  );
}

/** One award type's default letter holder, with who last changed it and its History. */
function RecipientRow({ type }: { type: LetterType }): ReactElement | null {
  const setting = useSetting('letters.defaultRecipient', type);
  const me = useQuery(meQuery).data;
  if (setting.status === 'hidden') return null;
  const words = (value: unknown): string => RECIPIENT[String(value)] ?? 'Not set';
  const unset = setting.status === 'ready' && RECIPIENT[String(setting.view.value)] === undefined;
  // Not set yet: greyed, with the way to where it's set for someone who can open it (APP-093).
  const canSet = me !== undefined && canOpenArea(me, 'elections-letters');
  return (
    <li className={s.recipientRow}>
      <span className={s.groupLabel}>{TYPE_NAME[type]}</span>
      {setting.status === 'ready' ? (
        <>
          {unset ? (
            <span className={s.muted}>
              Not set
              {canSet && (
                <>
                  {' · '}
                  <Link href={TEMPLATES_HREF} className={s.inlineLink}>
                    Set it in Settings › Elections and letters
                  </Link>
                </>
              )}
            </span>
          ) : (
            <span className={s.strong}>{words(setting.view.value)}</span>
          )}
          <SettingChange
            view={setting.view}
            title={`${TYPE_NAME[type]} · letter goes to`}
            subject="History · Letters"
            words={words}
          />
        </>
      ) : (
        <span className={s.muted}>
          {setting.status === 'loading' ? 'Loading…' : 'This didn’t load. Reload to try again.'}
        </span>
      )}
    </li>
  );
}

function WhoGetsItCard(): ReactElement {
  return (
    <Card
      title="Letter goes to"
      titleSize="sm"
      compact
      accent="gold"
      help={{
        shows:
          'Who each award type’s letter goes to unless you pick someone else when you release it. The award date decides when the employee sees it.',
        canDo:
          'Pick another holder for one person when you release their letter. The defaults are changed in Settings › Elections and letters.',
      }}
    >
      <ul className={s.recipients} aria-label="Who gets each letter by default">
        {LETTER_TYPES.map((type) => (
          <RecipientRow key={type} type={type} />
        ))}
      </ul>
    </Card>
  );
}

/** Where each fact in a letter comes from (the board's "Merge fields used" before any letter). */
function MergeSourcesCard(): ReactElement {
  const rows: [string, string][] = [
    ['Employee · EE#', 'From the directory'],
    ['Cash · options', 'From the check'],
    ['Vesting', 'From the award settings'],
    ['Tax table (PAA)', 'From Tax to enter · Estimate'],
    ['Signer', 'From the letter in use'],
  ];
  return (
    <Card
      title="Merge fields used"
      tone="dark"
      compact
      titleSize="sm"
      help={{
        shows: 'Where each fact in a letter comes from.',
        canDo:
          'Fix a wrong fact where it lives (the directory, the check or the template), then generate the letter again.',
      }}
    >
      <dl className={s.about}>
        {rows.map(([label, value]) => (
          <div key={label} className={s.aboutRow}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className={s.onDarkNote}>
        A cash-only letter has no options or vesting. Supporting files stay on the record and are
        not in the letter.
      </p>
    </Card>
  );
}

/** The Letters view's facts: the letters in use beside who gets each and where its facts come from. */
export function LettersFacts(): ReactElement {
  return (
    <TwoColumns
      sideLabel="Releasing letters"
      main={<LettersInUseCard />}
      side={
        <div className={s.stack}>
          <WhoGetsItCard />
          <MergeSourcesCard />
        </div>
      }
    />
  );
}
